import { type default as React, useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { cn, type ErrorKey } from '@dinewise/utils';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { LinkButton } from '../buttons/index.tsx';
import {
  EditPaymentDetailsModal,
  type EditPaymentDetailsModalHandle,
  type EditPaymentDetailsModalProps,
} from '../edit-payment-details-modal.tsx';
import {
  type AddPaymentMethodFetcherProps,
  AddPaymentMethodModal,
} from '../payment-method-modal.tsx';
import { UpdatePhoneNumberModal } from '../update-phone-number-modal.tsx';

type NotificationToastVariant = 'default' | 'prominent' | 'pill';

type CommonActionProps<T = Record<string, unknown>> = {
  onCloseAction: () => void;
  data?: T;
  toastVariant?: NotificationToastVariant;
};

type CallVenueComponentData = {
  venuePhoneNumber?: string;
};

type ViewEventComponentData = {
  eventId?: string;
  threadId?: string;
  /** Destination override for callers that land on a tab other than Messages. */
  to?: string;
  label?: string;
  openInNewTab?: boolean;
};

const underlinedViewEventAction =
  'h-auto w-fit gap-1 rounded-none border-0 bg-transparent p-0 font-semibold text-sm text-white underline underline-offset-2 hover:bg-transparent hover:text-white';

const viewEventActionStyles = {
  default: 'h-12 w-fit rounded-2xl px-5 py-2 font-semibold leading-4',
  prominent: underlinedViewEventAction,
  pill: underlinedViewEventAction,
} satisfies Record<NotificationToastVariant, string>;

type MessageThreadComponentData = {
  threadId?: string;
  eventId?: string;
  reservationId?: string;
  venueId?: string;
};

type HandleEditPaymentDetailsComponentData = {
  eventId?: string;
  paymentInstrumentId?: string;
};

const HandleEditPaymentDetailsComponent = ({
  onCloseAction,
  data,
}: CommonActionProps<HandleEditPaymentDetailsComponentData>) => {
  const modalRef = useRef<EditPaymentDetailsModalHandle>(null);

  const loaderFetcher = useFetcher();
  const [loaderData, setLoaderData] = useState<EditPaymentDetailsModalProps>();

  // If paymentInstrumentId is provided within data prop then use it, otherwise use the default value
  const paymentInstrumentId = data?.paymentInstrumentId;

  useEffect(() => {
    if (!loaderData && loaderFetcher.state === 'idle') {
      loaderFetcher.submit(null, { method: 'get', action: '/edit-payment-method' });
    }
  }, [loaderData, loaderFetcher]);

  useEffect(() => {
    if (loaderFetcher.data && Object.keys(loaderFetcher.data).length > 0) {
      setLoaderData(loaderFetcher.data as EditPaymentDetailsModalProps);
    }
  }, [loaderFetcher.data]);

  return (
    <>
      <span
        onClick={e => {
          e.preventDefault();
          if (modalRef.current) {
            modalRef.current.toggleModal();
          }
        }}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        className="font-medium text-red-800 text-sm"
      >
        Edit Payment Details
      </span>
      {loaderData && (
        <EditPaymentDetailsModal
          {...loaderData}
          ref={modalRef}
          onCloseHandler={() => {
            onCloseAction();
          }}
          instrumentId={paymentInstrumentId}
        />
      )}
    </>
  );
};

const HandleCreatePaymentMethodComponent = ({ onCloseAction }: CommonActionProps) => {
  const loaderFetcher = useFetcher();
  const fetcher = useFetcher();
  const [loaderData, setLoaderData] = useState<AddPaymentMethodFetcherProps>();
  const [isPaymentsFormModalOpen, setIsPaymentsFormModalOpen] = useState<boolean>(false);
  const actionRoute = '/create-payment-method';

  useEffect(() => {
    if (!loaderData && loaderFetcher.state === 'idle') {
      loaderFetcher.submit(null, { method: 'get', action: '/create-payment-method' });
    }
  }, [loaderData, loaderFetcher, loaderFetcher.state]);

  useEffect(() => {
    if (loaderFetcher.data && Object.keys(loaderFetcher.data).length > 0) {
      setLoaderData(loaderFetcher.data as AddPaymentMethodFetcherProps);
    }
  }, [loaderFetcher.data]);

  return (
    <>
      <span
        onClick={e => {
          e.preventDefault();
          setIsPaymentsFormModalOpen(true);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        className="font-medium text-red-800 text-sm"
      >
        Try Again
      </span>
      {loaderData && (
        <AddPaymentMethodModal
          {...{ actionRoute, fetcher, ...loaderData }}
          provider="FINIX"
          onCloseHandler={() => {
            setIsPaymentsFormModalOpen(false);
            onCloseAction();
          }}
          isModalOpen={isPaymentsFormModalOpen}
          supportedMethods={loaderData.clientPaymentMethods}
          defaultAccountCategory="personal"
        />
      )}
    </>
  );
};

const RefreshPageComponent = () => {
  return (
    <span
      onClick={e => {
        e.preventDefault();
        window.location.reload();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      className="font-medium text-red-800 text-sm"
    >
      Refresh
    </span>
  );
};

const UpdatePhoneNumberComponent = () => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  const onOpenModalHandler = useCallback(() => {
    setModalOpen(true);
  }, []);

  const onCloseModalHandler = useCallback(() => {
    setModalOpen(false);
  }, []);

  return (
    <>
      <span
        onClick={onOpenModalHandler}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        className="font-medium text-ande-error text-sm"
      >
        Update Phone Number
      </span>
      <UpdatePhoneNumberModal visible={isModalOpen} onCloseHandler={onCloseModalHandler} />
    </>
  );
};

const CallVenueComponent = ({ data }: CommonActionProps<CallVenueComponentData>) => {
  const venuePhoneNumber = data?.venuePhoneNumber;

  if (!venuePhoneNumber) {
    return null;
  }

  return (
    <LinkButton
      to={`tel:${venuePhoneNumber}`}
      rel="tel"
      theme="transparent"
      size="xs"
      className="w-fit p-0 font-medium text-red-800 text-sm"
    >
      Call Venue
    </LinkButton>
  );
};

const MessageThreadComponent = ({ data }: CommonActionProps<MessageThreadComponentData>) => {
  const threadId = data?.threadId;
  const eventId = data?.eventId;
  const reservationId = data?.reservationId as string;

  const to = eventId
    ? `/events/${eventId}/messages/${threadId}`
    : `/reservations/${reservationId}/messages`;

  return (
    <LinkButton
      {...{ to }}
      rel="tel"
      theme="transparent"
      size="xs"
      className="w-fit p-0 font-medium text-red-800 text-sm"
    >
      Message Venue
    </LinkButton>
  );
};

const ChangeHostComponent = ({ onCloseAction }: CommonActionProps) => {
  const handleClick = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openChangeHostModal'));
    onCloseAction();
  }, [onCloseAction]);

  return (
    <span
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleClick();
        }
      }}
      className="cursor-pointer font-medium text-red-800 text-sm"
    >
      Change the host
    </span>
  );
};

const ViewEventComponent = ({
  onCloseAction,
  data,
  toastVariant = 'default',
}: CommonActionProps<ViewEventComponentData>) => {
  const { eventId, threadId, to: toOverride, label = 'View', openInNewTab = false } = data ?? {};
  if (!eventId) {
    return null;
  }
  const messagesTo = threadId
    ? `/events/${eventId}/messages/${threadId}`
    : `/events/${eventId}/messages`;
  const to = toOverride ?? messagesTo;

  return (
    <LinkButton
      {...{ to }}
      onClick={() => {
        onCloseAction();
      }}
      theme="secondary"
      size="sm"
      target={openInNewTab ? '_blank' : undefined}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
      className={cn('shadow-none ring-0', viewEventActionStyles[toastVariant])}
    >
      {label}
      {openInNewTab ? <ArrowTopRightOnSquareIcon className="size-3.5" aria-hidden="true" /> : null}
    </LinkButton>
  );
};

type ViewMessageComponentData = {
  to?: string;
  /** Defaults to "View Message" */
  label?: string;
};

const ViewPaymentMethodsComponent = ({ onCloseAction }: CommonActionProps) => {
  return (
    <LinkButton
      to="/account-settings/payment-methods"
      onClick={() => {
        onCloseAction();
      }}
      theme="transparent"
      size="xs"
      className="w-fit p-0 font-medium text-ande-white text-sm border-none underline"
    >
      Manage Payment Methods
    </LinkButton>
  );
};

const ViewMessageComponent = ({
  onCloseAction,
  data,
}: CommonActionProps<ViewMessageComponentData>) => {
  const { to, label } = data ?? {};
  if (!to) {
    return null;
  }

  return (
    <LinkButton
      {...{ to }}
      theme="transparent"
      size="xs"
      className="w-fit p-0 font-medium text-ande-white text-sm border-none underline"
      onClick={() => {
        onCloseAction();
      }}
    >
      {label ?? 'View Message'}
    </LinkButton>
  );
};

export type ActionableErrorKey = ErrorKey;

export const ErrorKeyActions: Record<
  ActionableErrorKey,
  (props: {
    onCloseAction: () => void;
    data?: Record<string, unknown>;
    toastVariant?: NotificationToastVariant;
  }) => React.JSX.Element | null
> = {
  HandleEditPaymentDetails: HandleEditPaymentDetailsComponent,
  HandleCreatePaymentMethod: HandleCreatePaymentMethodComponent,
  RefreshPage: RefreshPageComponent,
  UpdatePhoneNumber: UpdatePhoneNumberComponent,
  CallVenue: CallVenueComponent,
  MessageVenue: MessageThreadComponent,
  ChangeHost: ChangeHostComponent,
  ViewEvent: ViewEventComponent,
  ViewMessage: ViewMessageComponent,
  ViewPaymentMethods: ViewPaymentMethodsComponent,
} as const;
